import type { PropsWithChildren, ReactNode } from 'react';
import { LoadingIndicator } from '@ui/components/application/loading-indicator/loading-indicator';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, PageHeader } from '@ui/console/primitives';

export function Page({
  title,
  description,
  actions,
  meta,
  children,
}: PropsWithChildren<{
  title: string;
  description?: string;
  actions?: ReactNode;
  meta?: ReactNode;
}>) {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        {...(description === undefined ? {} : { description })}
        {...(actions === undefined ? {} : { actions })}
        {...(meta === undefined ? {} : { meta })}
      />
      {children}
    </div>
  );
}

export function Actions({ children }: PropsWithChildren) {
  return <div className="flex flex-wrap items-center justify-end gap-2">{children}</div>;
}

export function ResourceState({
  loading,
  loadingLabel = 'workspace data',
  error,
  empty,
  emptyTitle = 'Nothing to show yet',
  emptyMessage = 'There is nothing to show yet.',
  onRetry,
  children,
}: PropsWithChildren<{
  loading: boolean;
  loadingLabel?: string;
  error?: string | undefined;
  empty?: boolean;
  emptyTitle?: string;
  emptyMessage?: string;
  onRetry?: () => void;
}>) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 py-10 text-sm text-tertiary">
        <LoadingIndicator type="dot-circle" size="sm" />
        Loading {loadingLabel}…
      </div>
    );
  }
  if (error) {
    return (
      <Callout
        tone="error"
        title="This workspace data could not be loaded"
        actions={
          onRetry ? (
            <Button size="sm" color="secondary" onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      >
        <FailureText raw={error} />
      </Callout>
    );
  }
  if (empty) {
    return (
      <Callout tone="neutral" title={emptyTitle}>
        {emptyMessage}
      </Callout>
    );
  }
  return children;
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <Callout tone="info" title="Scoped to your assignment">
      {children}
    </Callout>
  );
}
